import type { SegmentType } from '@rcs/data-schema';
import type { DecorationKind } from '@rcs/renderer';

/** 区間種別ごとの表示（色・アイコン）。色だけに頼らず、アイコンと文言（i18n）も併せて表示する。 */
export interface SegmentStyle {
  /** 3D の枕木の色（`0xrrggbb`） */
  readonly color: number;
  /** 画面の色（`#rrggbb`） */
  readonly css: string;
  /** 文言の前に付けるアイコン */
  readonly icon: string;
}

/** 通常・ブロック区間は、既定の枕木の色のまま（目印を付けない）。 */
export const SEGMENT_STYLES: Readonly<Partial<Record<SegmentType, SegmentStyle>>> = {
  lift: { color: 0x9333ea, css: '#9333ea', icon: '⛓' },
  launch: { color: 0x0891b2, css: '#0891b2', icon: '⚡' },
  brake: { color: 0xdc2626, css: '#dc2626', icon: '■' },
  station: { color: 0x16a34a, css: '#16a34a', icon: '▶' },
};

/** 表示する区間種別の順。 */
export const SEGMENT_STYLE_ORDER = ['station', 'lift', 'launch', 'brake'] as const;

/**
 * コースの制御点から、区間番号ごとの枕木の色を作る（制御点 `i` の種別が、区間 `i` の色）。
 * 色のない種別は `undefined`。
 */
export function segmentColors(
  controlPoints: readonly { readonly segment: SegmentType }[],
): (number | undefined)[] {
  return controlPoints.map((point) => SEGMENT_STYLES[point.segment]?.color);
}

/** 区間種別のうち、3D に設備（チェーン・フィン・ホームなど）を付けるもの。 */
const DECORATED = ['lift', 'launch', 'brake', 'station'] as const;

/** 制御点から、区間番号ごとの設備の種類を作る（設備のない種別は `undefined`）。 */
export function segmentDecorations(
  controlPoints: readonly { readonly segment: SegmentType }[],
): (DecorationKind | undefined)[] {
  return controlPoints.map((point) => DECORATED.find((kind) => kind === point.segment));
}
